import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetPatientStudiesQuery } from '@/state/slices/studies';
import type { PatientStudiesParams } from '@/models/study';
import type { DateRange, StudyType } from '../types/patientDetail';
import type { TimelineStudy } from '../types/studyTimeline';
import { mapApiStudyToTimeline } from '../utils/mapStudyResponse';
import { mapDateRangeToApi, mapStudyTypeToApi } from '../utils/filterParams';

export function usePatientStudies(
  patientId: string | undefined,
  dateRange: DateRange,
  studyType: StudyType,
  customDateRange: [string, string] | null = null
) {
  const params = useMemo<PatientStudiesParams | typeof skipToken>(() => {
    if (!patientId) return skipToken;

    const p: PatientStudiesParams = {
      patient_uuid: patientId,
      include: 'reports',
      paginate: false,
      ordering: '-updatedAt',
      ...mapDateRangeToApi(dateRange, customDateRange),
    };

    const studyTypeParam = mapStudyTypeToApi(studyType);
    if (studyTypeParam) p.study_type = studyTypeParam;

    return p;
  }, [patientId, dateRange, studyType, customDateRange]);

  const { data, isLoading, isError } = useGetPatientStudiesQuery(params);

  const studies = useMemo<TimelineStudy[]>(() => {
    if (!data || data.length === 0) return [];

    const latestIndex = data.reduce(
      (maxIdx, s, i, arr) =>
        new Date(s.updatedAt).getTime() >
        new Date(arr[maxIdx].updatedAt).getTime()
          ? i
          : maxIdx,
      0
    );

    return data.map((apiStudy, index) =>
      mapApiStudyToTimeline(apiStudy, index === latestIndex)
    );
  }, [data]);

  return { studies, isLoading, isError };
}
